import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../../services/api";
import { openOCI } from "../../utils/openOCI";
import ProcurementOrderLinesTable from "./ProcurementOrderLinesTable";
import procurementErrorMessage from "./procurementErrorMessage";

const EMPTY_LINE = { articleCode: "", description: "", quantity: 1, unit: "st", unitPrice: 0, notes: "" };

function toDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 16);
}

function extractObjectId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value?._id || value?.id || "");
  return String(value);
}

function normalizePayload(state) {
  return {
    type: state.type,
    associationType: state.associationType || "nog_te_koppelen",
    supplierId: state.supplierId || "",
    supplierName: state.supplierName || "",
    resourceType: state.resourceType || "",
    resourceId: state.resourceId || "",
    resourceName: state.resourceName || "",
    clientId: state.clientId || "",
    projectId: state.projectId || "",
    workOrderId: state.workOrderId || "",
    status: state.status || "concept",
    orderDate: state.orderDate || "",
    title: state.title,
    description: state.description,
    notes: state.notes,
    plannedStart: state.plannedStart || null,
    plannedEnd: state.plannedEnd || null,
    pricingModel: state.type === "service_order" ? state.pricingModel : "",
    rate: state.type === "service_order" && state.rate !== "" ? Number(state.rate) : null,
    expectedHours: state.type === "service_order" && state.expectedHours !== "" ? Number(state.expectedHours) : null,
    maxHours: state.type === "service_order" && state.maxHours !== "" ? Number(state.maxHours) : null,
    lines: state.type === "material_order" ? state.lines : [],
  };
}

export default function ProcurementOrderForm({
  initialValue = null,
  suppliers = [],
  clients = [],
  projects = [],
  workOrders = [],
  tuRequested = false,
  onSubmit,
  onCancel,
  submitting = false,
}) {
  const location = useLocation();
  const [state, setState] = useState({
    type: "material_order",
    associationType: "nog_te_koppelen",
    supplierId: "",
    supplierName: "",
    resourceType: "",
    resourceId: "",
    resourceName: "",
    clientId: "",
    projectId: "",
    workOrderId: "",
    status: "nog_te_koppelen",
    orderDate: new Date().toISOString().slice(0, 10),
    title: "",
    description: "",
    notes: "",
    plannedStart: "",
    plannedEnd: "",
    pricingModel: "hourly",
    rate: "",
    expectedHours: "",
    maxHours: "",
    lines: [EMPTY_LINE],
  });
  const [error, setError] = useState("");
  const [tuPunchoutLoading, setTuPunchoutLoading] = useState(false);
  const [tuPunchoutError, setTuPunchoutError] = useState("");
  const [tuPunchoutSuccess, setTuPunchoutSuccess] = useState("");

  useEffect(() => {
    if (!initialValue) return;
    setState({
      type: initialValue.type || "material_order",
      associationType: initialValue.associationType || "nog_te_koppelen",
      supplierId: initialValue.supplierId || "",
      supplierName: initialValue.supplierName || "",
      resourceType: initialValue.resourceType || "",
      resourceId: initialValue.resourceId || "",
      resourceName: initialValue.resourceName || "",
      clientId: initialValue.clientId || "",
      projectId: initialValue.projectId || "",
      workOrderId: initialValue.workOrderId || "",
      status: initialValue.status || "concept",
      orderDate: initialValue.orderDate ? String(initialValue.orderDate).slice(0, 10) : "",
      title: initialValue.title || "",
      description: initialValue.description || "",
      notes: initialValue.notes || "",
      plannedStart: toDateInput(initialValue.plannedStart),
      plannedEnd: toDateInput(initialValue.plannedEnd),
      pricingModel: initialValue.pricingModel || "hourly",
      rate: initialValue.rate ?? "",
      expectedHours: initialValue.expectedHours ?? "",
      maxHours: initialValue.maxHours ?? "",
      lines: Array.isArray(initialValue.lines) && initialValue.lines.length > 0 ? initialValue.lines : [EMPTY_LINE],
    });
  }, [initialValue]);

  const selectedSupplier = useMemo(
    () => suppliers.find((item) => String(item._id) === String(state.supplierId)),
    [suppliers, state.supplierId]
  );
  const selectedProject = useMemo(
    () => projects.find((item) => String(item?._id) === String(state.projectId)),
    [projects, state.projectId]
  );
  const selectedWorkOrder = useMemo(
    () => workOrders.find((item) => String(item?._id) === String(state.workOrderId)),
    [workOrders, state.workOrderId]
  );
  const inferredClientId = useMemo(() => {
    const explicitClient = extractObjectId(state.clientId);
    if (explicitClient) return explicitClient;

    if (String(state.associationType || "") === "project") {
      return extractObjectId(selectedProject?.clientId);
    }
    if (String(state.associationType || "") === "workorder") {
      const fromWorkorder = extractObjectId(selectedWorkOrder?.clientId);
      if (fromWorkorder) return fromWorkorder;
      return extractObjectId(selectedProject?.clientId);
    }
    return "";
  }, [state.clientId, state.associationType, selectedProject?.clientId, selectedWorkOrder?.clientId]);
  const availableSuppliers = useMemo(() => {
    const base = Array.isArray(suppliers)
      ? suppliers.filter((item) => item?.active !== false)
      : [];
    if (!inferredClientId) return base;

    const filtered = base.filter((item) => {
      const supplierClientId = extractObjectId(item?.clientId);
      return !supplierClientId || supplierClientId === inferredClientId;
    });

    const selectedSupplierId = String(state.supplierId || "");
    if (!selectedSupplierId) return filtered;
    if (filtered.some((item) => String(item?._id) === selectedSupplierId)) return filtered;
    const selected = suppliers.find((item) => String(item?._id) === selectedSupplierId);
    if (selected) return [...filtered, selected];
    return filtered;
  }, [suppliers, inferredClientId, state.supplierId]);

  useEffect(() => {
    if (!selectedSupplier) return;
    setState((prev) => ({ ...prev, supplierName: selectedSupplier.name || prev.supplierName }));
  }, [selectedSupplier]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (!state.title.trim()) {
      setError("Titel is verplicht.");
      return;
    }
    if (state.type === "material_order" && state.lines.length === 0) {
      setError("Voeg minimaal 1 orderregel toe.");
      return;
    }
    if (state.associationType === "workorder" && !String(state.workOrderId || "").trim()) {
      setError("Kies een werkbon bij 'Hoort bij'.");
      return;
    }
    if (state.associationType === "project" && !String(state.projectId || "").trim()) {
      setError("Kies een project bij 'Hoort bij'.");
      return;
    }
    if (!String(state.orderDate || "").trim()) {
      setError("Besteldatum is verplicht.");
      return;
    }
    if (
      ["voorraad_magazijn", "nog_te_koppelen"].includes(String(state.associationType || "")) &&
      !String(state.description || "").trim()
    ) {
      setError("Omschrijving / reden is verplicht voor vrije inkoop.");
      return;
    }
    if (
      ["voorraad_magazijn", "nog_te_koppelen"].includes(String(state.associationType || "")) &&
      !String(state.supplierId || state.supplierName || "").trim()
    ) {
      setError("Leverancier is verplicht voor vrije inkoop.");
      return;
    }

    try {
      await onSubmit?.(normalizePayload(state));
    } catch (err) {
      setError(procurementErrorMessage(err, err?.message || "Opslaan mislukt."));
    }
  };

  async function startTuPunchout() {
    const workOrderId =
      String(state.associationType || "") === "workorder"
        ? String(state.workOrderId || "").trim()
        : "";
    const projectId =
      String(state.associationType || "") === "project"
        ? String(state.projectId || "").trim()
        : "";

    setTuPunchoutLoading(true);
    setTuPunchoutError("");
    setTuPunchoutSuccess("");
    try {
      let procurementOrderId = String(initialValue?._id || "").trim();
      const payload = {};
      if (process.env.REACT_APP_TU_HOOK_URL) {
        payload.hookUrl = process.env.REACT_APP_TU_HOOK_URL;
      }

      let res = null;
      if (workOrderId) {
        payload.returnPath = `${location.pathname}${location.search || ""}`;
        res = await api.post(`/werkbonnen/${workOrderId}/integrations/tu/punchout`, payload);
      } else if (projectId) {
        payload.returnPath = `${location.pathname}${location.search || ""}`;
        res = await api.post(`/projects/${projectId}/integrations/tu/punchout`, payload);
      } else {
        if (!procurementOrderId) {
          const draftPayload = {
            ...normalizePayload(state),
            type: "material_order",
            associationType:
              state.associationType && ["voorraad_magazijn", "nog_te_koppelen"].includes(state.associationType)
                ? state.associationType
                : "nog_te_koppelen",
            status:
              state.associationType === "nog_te_koppelen"
                ? "nog_te_koppelen"
                : state.status || "concept",
            orderDate: state.orderDate || new Date().toISOString().slice(0, 10),
            title: String(state.title || "").trim() || "TU inkoopconcept",
            description:
              String(state.description || "").trim() || "OCI selectie Technische Unie",
            supplierName:
              selectedSupplier?.name ||
              String(state.supplierName || "").trim() ||
              "Technische Unie",
            lines: [],
          };
          const createRes = await api.post("/procurement-orders", draftPayload);
          procurementOrderId = String(createRes?.data?._id || "").trim();
          if (!procurementOrderId) {
            throw new Error("Concept inkooporder kon niet worden aangemaakt");
          }
          setTuPunchoutSuccess("Concept inkooporder aangemaakt, OCI wordt geopend...");
        }

        payload.returnPath = `/pl/inkoop/${procurementOrderId}`;
        res = await api.post(
          `/procurement-orders/${procurementOrderId}/integrations/tu/punchout`,
          payload
        );
      }
      const url = res?.data?.tu?.url;
      if (!openOCI(url)) {
        throw new Error("OCI catalogus-url ontbreekt");
      }
      setTuPunchoutSuccess("Catalogus openen...");
    } catch (err) {
      setTuPunchoutError(procurementErrorMessage(err, "OCI catalogus openen mislukt"));
    } finally {
      setTuPunchoutLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">
          {initialValue?._id ? "Inkooporder bewerken" : "Nieuwe inkooporder"}
        </h3>
        <div className="text-xs text-slate-500">V1 procurement</div>
      </div>

      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Type</span>
          <select
            value={state.type}
            onChange={(e) => setState((prev) => ({ ...prev, type: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="material_order">Materiaalbestelling</option>
            <option value="service_order">Service order (extern)</option>
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Hoort bij</span>
          <select
            value={state.associationType}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                associationType: e.target.value,
                workOrderId: e.target.value === "workorder" ? prev.workOrderId : "",
                projectId: e.target.value === "project" ? prev.projectId : "",
                status:
                  e.target.value === "nog_te_koppelen" && prev.status === "concept"
                    ? "nog_te_koppelen"
                    : ["workorder", "project"].includes(e.target.value) && prev.status === "nog_te_koppelen"
                      ? "concept"
                      : prev.status,
              }))
            }
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="workorder">werkbon</option>
            <option value="project">project</option>
            <option value="voorraad_magazijn">voorraad_magazijn</option>
            <option value="nog_te_koppelen">nog_te_koppelen</option>
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Leverancier</span>
          <select
            value={state.supplierId}
            onChange={(e) => setState((prev) => ({ ...prev, supplierId: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="">Geen selectie</option>
            {availableSuppliers.map((supplier) => (
              <option key={supplier._id} value={supplier._id}>
                {supplier.name}{supplier?.active === false ? " (inactief)" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm md:col-span-2">
          <span className="mb-1 block text-slate-600">Titel</span>
          <input
            value={state.title}
            onChange={(e) => setState((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
            placeholder="Bijv. Externe inspectie inzet week 12"
          />
        </label>

        <label className="text-sm md:col-span-2">
          <span className="mb-1 block text-slate-600">Omschrijving</span>
          <textarea
            value={state.description}
            onChange={(e) => setState((prev) => ({ ...prev, description: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
            rows={2}
          />
        </label>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Klant</span>
          <select value={state.clientId} onChange={(e) => setState((prev) => ({ ...prev, clientId: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2">
            <option value="">Geen</option>
            {clients.map((item) => <option key={item._id} value={item._id}>{item.naam}</option>)}
          </select>
        </label>
        {state.associationType === "project" ? (
          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Project</span>
            <select value={state.projectId} onChange={(e) => setState((prev) => ({ ...prev, projectId: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2">
              <option value="">Kies project</option>
              {projects.map((item) => <option key={item._id} value={item._id}>{item.naam || item.projectNaam || item.projectNummer}</option>)}
            </select>
          </label>
        ) : null}
        {state.associationType === "workorder" ? (
          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Werkbon</span>
            <select value={state.workOrderId} onChange={(e) => setState((prev) => ({ ...prev, workOrderId: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2">
              <option value="">Kies werkbon</option>
              {workOrders.map((item) => <option key={item._id} value={item._id}>{item.werkbonNummer || item.werkbonnummer || item.titel || item.title}</option>)}
            </select>
          </label>
        ) : null}
        {["voorraad_magazijn", "nog_te_koppelen"].includes(String(state.associationType || "")) ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Deze inkoopbon wordt zonder project/werkbon opgeslagen en kan later worden gekoppeld.
          </div>
        ) : null}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Status</span>
          <select
            value={state.status}
            onChange={(e) => setState((prev) => ({ ...prev, status: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          >
            <option value="concept">concept</option>
            <option value="besteld">besteld</option>
            <option value="opgehaald">opgehaald</option>
            <option value="ontvangen">ontvangen</option>
            <option value="nog_te_koppelen">nog_te_koppelen</option>
            <option value="gekoppeld">gekoppeld</option>
            <option value="gecontroleerd">gecontroleerd</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Besteldatum</span>
          <input
            type="date"
            value={state.orderDate}
            onChange={(e) => setState((prev) => ({ ...prev, orderDate: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 px-3 py-2"
          />
        </label>
      </div>

      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-emerald-900">OCI catalogus (Technische Unie)</p>
            <p className="text-xs text-emerald-800">
              Open de OCI catalogus vanuit deze bestelling. Voor OCI-import is een werkbon of project nodig.
              Zonder koppeling kun je deze inkooporder wel gewoon handmatig aanmaken en opslaan.
            </p>
          </div>
          <button
            type="button"
            onClick={startTuPunchout}
            disabled={tuPunchoutLoading}
            className="rounded-lg bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            {tuPunchoutLoading ? "Catalogus openen..." : "Open OCI catalogus"}
          </button>
        </div>
        {tuRequested && !state.workOrderId && !state.projectId ? (
          <p className="mt-2 text-xs text-amber-800">
            OCI-flow actief zonder context: koppelen aan werkbon/project is alleen nodig voor OCI, niet voor het opslaan van een losse inkooporder.
          </p>
        ) : null}
        {tuPunchoutError ? <p className="mt-2 text-xs text-red-700">{tuPunchoutError}</p> : null}
        {tuPunchoutSuccess ? <p className="mt-2 text-xs text-emerald-800">{tuPunchoutSuccess}</p> : null}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Geplande start</span>
          <input type="datetime-local" value={state.plannedStart} onChange={(e) => setState((prev) => ({ ...prev, plannedStart: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Geplande eind</span>
          <input type="datetime-local" value={state.plannedEnd} onChange={(e) => setState((prev) => ({ ...prev, plannedEnd: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
        </label>
      </div>

      {state.type === "service_order" && (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-sm font-medium text-slate-800">Externe inzet (resource, niet per se interne user)</p>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Resource type</span>
              <select value={state.resourceType} onChange={(e) => setState((prev) => ({ ...prev, resourceType: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2">
                <option value="">Geen</option>
                <option value="external_person">external_person</option>
                <option value="external_team">external_team</option>
                <option value="supplier">supplier</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Resource id</span>
              <input value={state.resourceId} onChange={(e) => setState((prev) => ({ ...prev, resourceId: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" placeholder="Externe referentie/id" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Resource naam</span>
              <input value={state.resourceName} onChange={(e) => setState((prev) => ({ ...prev, resourceName: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" placeholder="Naam zzp'er of team" />
            </label>
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Prijsmodel</span>
              <select value={state.pricingModel} onChange={(e) => setState((prev) => ({ ...prev, pricingModel: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2">
                <option value="hourly">hourly</option>
                <option value="fixed">fixed</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Tarief (€)</span>
              <input type="number" min="0" step="0.01" value={state.rate} onChange={(e) => setState((prev) => ({ ...prev, rate: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Verwachte uren</span>
              <input type="number" min="0" step="0.1" value={state.expectedHours} onChange={(e) => setState((prev) => ({ ...prev, expectedHours: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Max uren</span>
              <input type="number" min="0" step="0.1" value={state.maxHours} onChange={(e) => setState((prev) => ({ ...prev, maxHours: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
            </label>
          </div>
        </div>
      )}

      {state.type === "material_order" && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-800">Orderregels</p>
          <ProcurementOrderLinesTable lines={state.lines} editable onChange={(lines) => setState((prev) => ({ ...prev, lines }))} />
        </div>
      )}

      <label className="block text-sm">
        <span className="mb-1 block text-slate-600">Notities</span>
        <textarea value={state.notes} onChange={(e) => setState((prev) => ({ ...prev, notes: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" rows={2} />
      </label>

      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700">
          Annuleren
        </button>
        <button type="submit" disabled={submitting} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
          {submitting ? "Opslaan..." : "Opslaan"}
        </button>
      </div>
    </form>
  );
}
